import { useState } from "react";

/**
 * Hook to handle single and double click events with different callbacks
 * @template S Type for single click function arguments
 * @template D Type for double click function arguments
 * @param singleClickFn Callback for single click events
 * @param doubleClickFn Callback for double click events
 * @param delay Delay in ms to distinguish between single and double clicks
 */
function useDoubleClick<S = unknown, D = unknown>(
  singleClickFn: (args: S) => void,
  doubleClickFn: (args: D) => void,
  delay = 250
) {
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

  const handleClick = (args: S) => {
    if (timeoutId) {
      clearTimeout(timeoutId); // Clear any previous timeout
    }

    const id = setTimeout(() => {
      singleClickFn(args); // Trigger single click action after delay
    }, delay);

    setTimeoutId(id);
  };

  const handleDblClick = (args: D) => {
    if (timeoutId) {
      clearTimeout(timeoutId); // Clear the single-click timeout if double-clicked
    }
    doubleClickFn(args); // Trigger double-click action
  };

  return { handleClick, handleDblClick };
}

export default useDoubleClick;
